/**
 * Per-screen display-options state: what survives leaving the screen and reloading the
 * app. VIEW state only — this never goes anywhere near movements/item_edits.
 *
 * Two layers, on purpose:
 *  - a module-level Map, because app.tsx keys screens on the route and a detail→back
 *    round-trip REMOUNTS the list (the `platform/list-position.ts` precedent) — the Map
 *    is what makes "back returns to the view you left" true within a session;
 *  - `localStore` under a versioned key, so the preference survives a reload — the third
 *    persisted preference in the app, after `mononoke.theme` and `mononoke.language`.
 *
 * The stored copy outlives code changes, so readers must run it through the engine's
 * `sanitizeConfig` against the CURRENT spec before trusting it; this module only
 * guarantees the JSON shape, not that the axes still exist.
 */
import { localStore } from '../platform/browser.js';
import type { DisplayConfig } from './engine.js';

export interface StoredDisplay {
  v: 3;
  config: DisplayConfig;
  /** Collapsed section paths (see GroupSection.path). */
  collapsed: string[];
}

const mem = new Map<string, StoredDisplay>();

/**
 * v2 (0.1.487, maintainer 8 Sep 2026): every display starts flat, on EVERY device — including
 * the ones that had been carrying the seeded Category ▸ Status since 0.1.432. A new key is how
 * a stored preference is reset without touching anyone's storage: the v1 entries are simply
 * never read again. The trade-off was ruled: a view someone composed by hand on their phone is
 * rebuilt once, and saved views (081, server-side) are untouched by this.
 */
/*
 * v3 (0.1.522, maintainer 1 Oct 2026): the reverse turn. Every catalogue sheet now OPENS grouped
 * and sorted — Items Category ▸ Status, Boxes Project ▸ Source ▸ Location, Outfits Rack ▸ Project,
 * Reservations State ▸ Project, Check-outs Due back ▸ Project — and the ruling was to reset once so
 * those defaults land on every device, not only on a fresh one. Same mechanism, same trade-off as
 * v2: a hand-composed view is rebuilt once; saved views (081, server-side) are untouched.
 */
const keyFor = (screen: string): string => `mononoke.display.v3.${screen}`;

export function loadDisplayState(screen: string): StoredDisplay | null {
  const inMem = mem.get(screen);
  if (inMem !== undefined) return inMem;
  if (localStore === null) return null;
  try {
    const raw = localStore.getItem(keyFor(screen));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const p = parsed as Partial<StoredDisplay>;
    if (p.v !== 3 || p.config === undefined) return null;
    const stored: StoredDisplay = {
      v: 3,
      config: p.config,
      collapsed: Array.isArray(p.collapsed) ? p.collapsed.filter((x): x is string => typeof x === 'string') : [],
    };
    mem.set(screen, stored);
    return stored;
  } catch {
    // A corrupt entry is a preference, not data — fall back to defaults silently.
    return null;
  }
}

export function saveDisplayState(screen: string, stored: StoredDisplay): void {
  mem.set(screen, stored);
  if (localStore === null) return;
  try {
    localStore.setItem(keyFor(screen), JSON.stringify(stored));
  } catch {
    // Quota/denied: the in-session Map still works; losing cross-reload persistence of a
    // view preference is not worth a banner (G10 covers operations, not preferences).
  }
}

/**
 * THE FILTERS SURVIVE TOO (0.1.522, maintainer 1 Oct 2026: "Applying filters should remain through
 * tab switches and app reloads, the same for group and sort"). Until now every sheet held its
 * filters in component state — a tab switch remounted the screen and they were gone.
 *
 * ⚠️ KEPT APART FROM `StoredDisplay.config` ON PURPOSE: a saved view (081) is presentation only —
 * group, sort, direction — and its match test compares configs. Folding filters into the config
 * would make every filtered sheet read as "not your saved view". So: one key per SCREEN (not per
 * width — the desk and the phone list are one filter state in every screen that has both), its own
 * in-session Map for the same remount reason as above.
 *
 * The value is opaque here. The stored copy outlives code changes, so each screen runs it through
 * its own sanitiser (`sanitizeAxisFilters`, `sanitizeItemFilters`, `sanitizeBoxFilters`) before
 * trusting it — a bucket that no longer exists is dropped, never thrown.
 */
const filterKeyFor = (screen: string): string => `mononoke.filters.v1.${screen}`;
const memFilters = new Map<string, unknown>();

/** `undefined` = nothing stored (the screen's default applies); anything else is unvalidated. */
export function loadDisplayFilters(screen: string): unknown {
  if (memFilters.has(screen)) return memFilters.get(screen);
  if (localStore === null) return undefined;
  try {
    const raw = localStore.getItem(filterKeyFor(screen));
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    memFilters.set(screen, parsed);
    return parsed;
  } catch {
    // A corrupt entry is a preference, not data — the screen's default applies.
    return undefined;
  }
}

export function saveDisplayFilters(screen: string, filters: unknown): void {
  memFilters.set(screen, filters);
  if (localStore === null) return;
  try {
    localStore.setItem(filterKeyFor(screen), JSON.stringify(filters));
  } catch {
    // Quota/denied: the in-session Map still carries the filters across a tab switch.
  }
}
